import { BRAND } from "@/data/constants"
import { Product } from "@/services"
import { GA_TRACKING_ID } from "@/services/Configuration"
import { error, log } from "@/services/Log"
import { ProductType } from "@/types"
import { CouponType, PhysicalItem } from "@/types/Cart"
import { AddressType } from "@/types/CustomerInfo"

type CategoryType = { name: string, path: string }
export const getCategoryName = (categories: Array<CategoryType>) => [...categories].sort((a, b) => {
  const pathDiff = b.path.split('/').length - a.path.split('/').length
  return pathDiff || (b.name.length - a.name.length)
})[0]?.name || ''

type productGAInput = {
  name: string,
  sku: string,
  price: string,
  salePrice: number,
  variantId: number,
  categories: CategoryType[],
  event: string,
  brand: string,
  quantity?: number,
  customerId?: number,
  customerEmail?: string,
  cartTotal: number
}

export const productGA: (i: productGAInput) => void = ({
  name,
  sku,
  price,
  salePrice,
  variantId,
  categories,
  event,
  brand,
  quantity,
  customerId = 0,
  customerEmail = '',
  cartTotal
}) => {
  try {
    log(`productGA recording`, { name, sku, price, salePrice, variantId, categories, event, brand, quantity, customerId, customerEmail, cartTotal })
    const keyName = (event === 'addToCart') ? 'add' : 'detail'
    const dataLayerEvent = {
      event,
      ecommerce: {
        currencyCode: 'USD',
        [keyName]: {
          products: [{
            name,
            id: sku, // for grouped products like quilts, do you have a default that you can use?
            price, // for grouped products like quilts, do you have a default that you can use?
            salePrice, // for grouped products like quilts, do you have a default that you can use?
            brand: brand,
            variant: variantId,
            category: getCategoryName(categories), // category visited in from
            totalReviews: '',
            averageRating: '',
            inventoryCount: 'In Stock',
            quantity: `${typeof quantity !== 'undefined' ? quantity : ''}`
          }],
        },
      },
      cartTotal,
      customerId,
      customerEmail,
      visitorType: customerEmail ? "Logged In" : "Guest",
      pageName: 'pdp',
    }
    window['dataLayer'].push({ ecommerce: null }) // Clear the previous ecommerce object.
    window['dataLayer'].push(dataLayerEvent)
  } catch (err) {
    error(`productGA Failed ${err}`)
  }
}

const getActualPrice = (item: PhysicalItem, purchase: boolean) => {
  let price = item.salePrice
  if (!!item?.discounts?.length) {
    const coupon = purchase ? item.couponAmount : item.discounts[0].discountedAmount
    price = parseFloat((item.salePrice - (coupon / item.quantity)).toFixed(2))
  }
  return price
}

export const gaProducts = async (lineItems: PhysicalItem[], purchase = false) => {

  const skus = lineItems.map((item) => item.sku).filter(id => !!id)
  const skusData = await Product.bySkus(skus)

  const itemMetadata: { [key: string]: ProductType } = {}

  skusData.forEach(p => {
    itemMetadata[p.id] = p
    itemMetadata[p.sku] = p
    p.variants.forEach(v => itemMetadata[v.sku] = p)
  })

  const categoryName = (sku: string) => [...(itemMetadata[sku]?.category_info || [])]?.sort((a, b) => {
    const pathDiff = b.path.split('/').length - a.path.split('/').length
    return pathDiff || (b.name.length - a.name.length)
  })?.[0]?.name

  const brandName = (sku) => itemMetadata?.[sku]?.brand || ''

  return lineItems.map((item) => {
    return { // List of productFieldObjects.
      name: item.name,
      id: item.sku,
      salePrice: item.salePrice, // original price paid for product before discount
      price: getActualPrice(item, purchase), // net price paid for product per quantity
      brand: brandName(item?.sku),
      category: item?.productId ? categoryName(item?.sku) : '',
      variant: item.variantId, // "Small" | "Large" | etc
      quantity: item.quantity,
    }
  })
}

type PurchaseEventGA = (input: {
  orderId: number
  billingAddress: AddressType,
  coupons: Array<CouponType>,
  totalDiscount: number,
  lineItems: PhysicalItem[],
  shippingCost: number,
  email: string,
  taxTotal: number,
  grandTotal: number,
  cartTotal: number,
}) => Promise<{ success: boolean }>

export const purchaseEventGA: PurchaseEventGA = async ({
  orderId,
  billingAddress,
  coupons,
  totalDiscount,
  lineItems,
  email,
  shippingCost,
  taxTotal,
  grandTotal,
  cartTotal,
}) => {
  return new Promise(async (resolve) => {

    let timeout = setTimeout(() => {
      error(`GA event didn't complete under 7.5s TIMEOUT`)
      resolve({ success: false })
    }, 7500) // 7.5s is the timeout to report the purchase event to GA
    try {
      log(`purchaseEventGA ${JSON.stringify({
        orderId,
        billingAddress,
        coupons,
        totalDiscount,
        lineItems,
        email,
        shippingCost,
        taxTotal,
        grandTotal
      })}`)
      const products = await gaProducts(lineItems, true)
      const discountFromCoupons = (
        Array.isArray(coupons) ?
          coupons.reduce((total, { discountedAmount }) => discountedAmount + total, 0) :
          0
      ) || 0
      const pushDone = async (containerId) => {
        log(`Purchase eventCallback ${JSON.stringify({ containerId, GA_TRACKING_ID })}`)
        if (containerId === GA_TRACKING_ID) {
          resolve({ success: true })
          clearTimeout(timeout)
        } else {
          log('Wrong container ID')
        }
      }
      const purchaseEvent = {
        event: 'purchase',
        eventCallback: pushDone,
        eventTimeout: 8500,
        ecommerce: {
          customerEmail: email,
          customerFirstName: billingAddress.firstName,
          customerLastName: billingAddress.lastName,
          customerPhone: billingAddress.phone,
          customerCity: billingAddress.city,
          customerZip: billingAddress.zip,
          customerAddress1: billingAddress.street1,
          customerAddress2: billingAddress.street2,
          customerCountryCode: billingAddress.countryCode || 'US',
          customerState: billingAddress.stateName || billingAddress.state || billingAddress.stateAcronym,
          customerOrdersCount: '',
          customerTotalSpent: '',
          pageName: 'order confirmation', ///order-confirmation page (/order-confirmation)
          purchase: {
            actionField: {
              id: orderId, // Transaction ID
              affiliation: BRAND,
              revenue: grandTotal, // Total transaction value (incl. tax and shipping)
              tax: taxTotal,
              shipping: shippingCost,
              coupon: coupons?.[0]?.code || '',
              couponAmount: discountFromCoupons,
              totalDiscount: totalDiscount,
              cartTotal,
            },
            products,
          },
        },
      }
      log('GA push purchase', { purchaseEvent })
      window['dataLayer'].push({ ecommerce: null }) // Clear the previous ecommerce object.
      window['dataLayer'].push(purchaseEvent)
    } catch (err) {
      error('Failed to submit conversion to GA: ' + err)
      resolve({ success: false })
    }
  })
}
